import React, { useState, useEffect } from 'react';
import { ArrowDownTrayIcon, DocumentIcon, ClockIcon, PlusIcon, CheckCircleIcon, XCircleIcon, SparklesIcon, CpuChipIcon, TrashIcon, CodeBracketIcon, BoltIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { collection, query, where, orderBy, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { ClearAdminGenerator } from '../../utils/clearAdminGenerator';
import { ClearAdminGenerationResult } from '../../types/clearAdmin';
import { energiaService } from '../../services/energiaService';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface ClearAdminFile {
  id: string;
  name: string;
  size: number;
  type: 'xml' | 'csv';
  lastModified: string;
  humanSize: string;
  downloadUrl: string;
  generated_at: string;
}

interface GenerationRequest {
  id: string;
  currency: string;
  month: number;
  year: number;
  provider?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  error?: string;
  result?: any;
}

interface ClearAdminDownloaderProps {
  className?: string;
}

const ClearAdminDownloader: React.FC<ClearAdminDownloaderProps> = ({ className = '' }) => {
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState<'generate' | 'download'>('generate');
  const [files, setFiles] = useState<ClearAdminFile[]>([]);
  const [requests, setRequests] = useState<GenerationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<ClearAdminFile | null>(null);

  // TypeScript generator state
  const [generationMode, setGenerationMode] = useState<'python' | 'typescript'>('typescript');
  const [generatedXML, setGeneratedXML] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<ClearAdminGenerationResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    currency: 'HUF',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    provider: ''
  });

  useEffect(() => {
    if (activeTab === 'download') {
      loadFiles();
    } else {
      loadRequests();
    }
  }, [activeTab]);

  const loadFiles = async () => {
    try {
      console.log('📁 Loading Clear Admin files from Firestore...');
      setLoading(true);
      setError(null);
      
      // Query all files (we'll sort manually on frontend to handle mixed timestamp formats)
      const filesQuery = query(
        collection(db, 'clear_admin_exports')
      );
      
      console.log('🔍 Executing Firestore query...');
      const querySnapshot = await getDocs(filesQuery);
      const fileList: ClearAdminFile[] = [];
      
      console.log(`📋 Found ${querySnapshot.docs.length} documents in clear_admin_exports`);
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 Processing document:', { 
          id: doc.id, 
          filename: data.filename,
          generated_at: data.generated_at,
          generated_at_type: typeof data.generated_at,
          data 
        });
        
        // Handle both old (string) and new (timestamp) generated_at formats
        let lastModified: string;
        if (data.generated_at) {
          if (typeof data.generated_at === 'string') {
            lastModified = data.generated_at;
          } else if (data.generated_at.toDate) {
            lastModified = data.generated_at.toDate().toISOString();
          } else {
            lastModified = new Date().toISOString();
          }
        } else if (data.lastModified) {
          if (typeof data.lastModified === 'string') {
            lastModified = data.lastModified;
          } else if (data.lastModified.toDate) {
            lastModified = data.lastModified.toDate().toISOString();
          } else {
            lastModified = new Date().toISOString();
          }
        } else {
          lastModified = new Date().toISOString();
        }
        
        fileList.push({
          id: doc.id,
          name: data.filename || data.name || 'Ismeretlen fájl',
          size: data.size || 0,
          type: data.type || 'xml',
          lastModified: lastModified,
          humanSize: formatFileSize(data.size || 0),
          downloadUrl: data.storage_url || data.downloadUrl || '',
          generated_at: data.generated_at
        });
      });
      
      // Manual sort to ensure newest files are first (handle both string and timestamp formats)
      fileList.sort((a, b) => {
        const dateA = new Date(a.lastModified);
        const dateB = new Date(b.lastModified);
        return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
      });
      
      console.log('✅ File list prepared and sorted:', fileList.map(f => ({ name: f.name, lastModified: f.lastModified })));
      setFiles(fileList);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Hiba a fájlok betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const requestsQuery = query(
        collection(db, 'clear_admin_requests'),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(requestsQuery);
      const requestList: GenerationRequest[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        requestList.push({
          id: doc.id,
          currency: data.currency || 'HUF',
          month: data.month || new Date().getMonth() + 1,
          year: data.year || new Date().getFullYear(),
          provider: data.provider,
          status: data.status || 'pending',
          created_at: data.created_at,
          error: data.error,
          result: data.result
        });
      });
      
      setRequests(requestList);
    } catch (err) {
      console.error('Error loading requests:', err);
      setError('Hiba a kérések betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (generationMode === 'typescript') {
      // TypeScript azonnali generálás
      await handleTypescriptGeneration();
    } else {
      // Python backend kérés
      await handlePythonRequest();
    }
  };

  const handlePythonRequest = async () => {
    try {
      setSubmittingRequest(true);
      setError(null);
      
      const requestData = {
        currency: formData.currency,
        month: formData.month,
        year: formData.year,
        provider: formData.provider || null,
        status: 'pending',
        created_at: new Date().toISOString(),
        requested_by: 'user'
      };
      
      await addDoc(collection(db, 'clear_admin_requests'), requestData);
      await loadRequests();
      
      setFormData({
        currency: 'HUF',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        provider: ''
      });
      
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Hiba a kérés küldésekor');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleTypescriptGeneration = async () => {
    try {
      console.log('🚀 Starting TypeScript generation...', { formData });
      setIsGenerating(true);
      setError(null);
      setGeneratedXML(null);
      setGenerationResult(null);

      // Számlázási adatok lekérése
      console.log('📊 Fetching billing data...', {
        provider: formData.provider || undefined,
        month: formData.month,
        year: formData.year
      });
      
      const billingData = await energiaService.getBillingDataForClearAdmin(
        formData.provider || undefined,
        formData.month,
        formData.year
      );

      console.log('📋 Billing data received:', { count: billingData.length, data: billingData });

      if (billingData.length === 0) {
        setError(`Nincsenek számlázási adatok a megadott szűrőkkel: ${formData.provider || 'összes szolgáltató'}, ${formData.year}/${formData.month}`);
        return;
      }

      // XML generálás TypeScript-tel
      console.log('🔨 Creating XML generator...');
      const generator = new ClearAdminGenerator({
        currency: formData.currency as 'HUF' | 'EUR'
      });

      console.log('⚙️ Generating XML...', {
        currency: formData.currency,
        month: formData.month,
        year: formData.year,
        provider: formData.provider || undefined,
        recordCount: billingData.length
      });

      const result = generator.generateXML(billingData, {
        currency: formData.currency as 'HUF' | 'EUR',
        month: formData.month,
        year: formData.year,
        provider: formData.provider || undefined
      });

      console.log('✅ Generation result:', result);
      setGenerationResult(result);

      if (result.success && result.xml) {
        setGeneratedXML(result.xml);
        console.log('✅ XML generated successfully, length:', result.xml.length);
      } else {
        const errorMsg = result.errors?.join(', ') || 'XML generálási hiba';
        console.error('❌ XML generation failed:', errorMsg);
        setError(errorMsg);
      }

    } catch (err) {
      console.error('💥 Fatal error in TypeScript generation:', err);
      setError(`Hiba az XML generálása során: ${err instanceof Error ? err.message : 'Ismeretlen hiba'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadGeneratedXML = () => {
    if (!generatedXML || !generationResult) return;

    const blob = new Blob([generatedXML], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generationResult.fileName || `clear_admin_${formData.currency}_${new Date().toISOString().split('T')[0]}.xml`;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (file: ClearAdminFile) => {
    try {
      setDownloadingFile(file.name);
      
      // Fetch the file data
      const response = await fetch(file.downloadUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the file blob
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Hiba a fájl letöltésekor');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDelete = (file: ClearAdminFile) => {
    setFileToDelete(file);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    
    try {
      setDeletingFile(fileToDelete.name);
      setError(null);
      
      // Try to delete from Firebase Storage (ignore if file doesn't exist)
      try {
        const storage = getStorage();
        const storageRef = ref(storage, `clear_admin_exports/${fileToDelete.name}`);
        await deleteObject(storageRef);
      } catch (storageError: any) {
        // If file doesn't exist in storage, that's okay - just log it
        if (storageError?.code === 'storage/object-not-found') {
          console.warn(`File ${fileToDelete.name} not found in Storage, cleaning up Firestore only`);
        } else {
          throw storageError; // Re-throw other storage errors
        }
      }
      
      // Delete from Firestore (this should always work)
      await deleteDoc(doc(db, 'clear_admin_exports', fileToDelete.id));
      
      // Refresh the files list
      await loadFiles();
      
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Hiba a fájl törlésekor');
    } finally {
      setDeletingFile(null);
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('hu-HU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getFileIcon = (type: string) => {
    return <DocumentIcon className="w-5 h-5 text-blue-600" />;
  };

  const getFileTypeColor = (type: string) => {
    switch (type) {
      case 'xml':
        return 'bg-blue-100 text-blue-800';
      case 'csv':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCurrencyIcon = (currency: string) => {
    return currency === 'EUR' ? '€' : 'Ft';
  };

  const getProviderColor = (provider: string) => {
    const colors: { [key: string]: string } = {
      'elmu': 'bg-blue-100 text-blue-800',
      'eon': 'bg-green-100 text-green-800',
      'demasz': 'bg-yellow-100 text-yellow-800',
      'opus': 'bg-purple-100 text-purple-800',
      'mvmi': 'bg-indigo-100 text-indigo-800',
      'eon_mvm': 'bg-orange-100 text-orange-800'
    };
    return colors[provider] || 'bg-gray-100 text-gray-800';
  };

  const renderGenerateTab = () => (
    <div className="p-3 space-y-3">
      {/* Compact Stats */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
          <div className="flex items-center space-x-1">
            <SparklesIcon className="w-3 h-3 text-blue-600" />
            <div>
              <p className="text-blue-700 text-xs font-medium">Aktív</p>
              <p className="text-blue-900 text-sm font-bold">
                {requests.filter(r => r.status === 'pending' || r.status === 'processing').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-2 border border-green-200">
          <div className="flex items-center space-x-1">
            <CheckCircleIcon className="w-3 h-3 text-green-600" />
            <div>
              <p className="text-green-700 text-xs font-medium">Kész</p>
              <p className="text-green-900 text-sm font-bold">
                {requests.filter(r => r.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-2 border border-purple-200">
          <div className="flex items-center space-x-1">
            <CpuChipIcon className="w-3 h-3 text-purple-600" />
            <div>
              <p className="text-purple-700 text-xs font-medium">Export</p>
              <p className="text-purple-900 text-sm font-bold">{files.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-2 border border-orange-200">
          <div className="flex items-center space-x-1">
            <ClockIcon className="w-3 h-3 text-orange-600" />
            <div>
              <p className="text-orange-700 text-xs font-medium">Utolsó</p>
              <p className="text-orange-900 text-xs font-medium">
                                        {requests.length > 0 ? formatDate(requests[0].created_at) : 'Nincs'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Mode Selector */}
      <div className="bg-gray-50 rounded-lg p-3 border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CpuChipIcon className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Generálási mód:</span>
          </div>
          <div className="flex bg-white rounded-md border">
            <button
              onClick={() => setGenerationMode('typescript')}
              className={`px-3 py-1 text-xs font-medium rounded-l-md transition-colors ${
                generationMode === 'typescript'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              ⚡ Azonnali
            </button>
            <button
              onClick={() => setGenerationMode('python')}
              className={`px-3 py-1 text-xs font-medium rounded-r-md transition-colors ${
                generationMode === 'python'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              🐍 Backend
            </button>
          </div>
        </div>
      </div>

      {/* Compact Generation Form */}
      <div className="bg-white rounded-lg p-4 border">
        <div className="flex items-center space-x-2 mb-3">
          <PlusIcon className="w-4 h-4 text-blue-600" />
          <h4 className="text-sm font-semibold text-gray-800">XML Generálás</h4>
        </div>
        
        <form onSubmit={handleSubmitRequest} className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Valuta</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({...formData, currency: e.target.value})}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="HUF">HUF</option>
                <option value="EUR">EUR</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hónap</label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({...formData, month: parseInt(e.target.value)})}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                {Array.from({length: 12}, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleDateString('hu-HU', { month: 'short' })}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Év</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                min="2020"
                max="2030"
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Szolgáltató</label>
              <select
                value={formData.provider}
                onChange={(e) => setFormData({...formData, provider: e.target.value})}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">Összes</option>
                <option value="elmu">ELMU</option>
                <option value="eon">EON</option>
                <option value="demasz">DÉMÁSZ</option>
                <option value="opus">OPUS</option>
                <option value="mvmi">MVMI</option>
                <option value="eon_mvm">EON-MVM</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={submittingRequest || isGenerating}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {(submittingRequest || isGenerating) ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                <span>{generationMode === 'typescript' ? 'Generálás...' : 'Küldés...'}</span>
              </>
            ) : (
              <>
                {generationMode === 'typescript' ? (
                  <>
                    <BoltIcon className="w-3 h-3" />
                    <span>Azonnali Generálás</span>
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-3 h-3" />
                    <span>Backend Kérés</span>
                  </>
                )}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Compact XML Preview */}
      {generationMode === 'typescript' && (
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <CodeBracketIcon className="w-4 h-4 text-green-600" />
              <h4 className="text-sm font-semibold text-gray-800">XML Előnézet</h4>
            </div>
            {generatedXML && (
              <button
                onClick={handleDownloadGeneratedXML}
                className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                Letöltés
              </button>
            )}
          </div>
          
          <div className="border border-gray-200 rounded bg-gray-50 h-48 overflow-y-auto">
            {generatedXML ? (
              <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono p-2 leading-tight">
                {generatedXML}
              </pre>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <CodeBracketIcon className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs font-medium">XML Előnézet</p>
                  <p className="text-xs mt-1">Generálás után jelenik meg</p>
                </div>
              </div>
            )}
          </div>

          {/* Compact Generation Result */}
          {generationResult && (
            <div className="mt-2 grid grid-cols-4 gap-2">
              <div className="bg-blue-50 rounded p-2 border border-blue-200">
                <div className="text-blue-700 text-xs font-medium">Rekord</div>
                <div className="text-blue-900 text-sm font-bold">
                  {generationResult.recordCount || 0}
                </div>
              </div>
              
              <div className="bg-green-50 rounded p-2 border border-green-200">
                <div className="text-green-700 text-xs font-medium">Összeg</div>
                <div className="text-green-900 text-sm font-bold">
                  {generationResult.totalAmount?.toFixed(0) || '0'} {generationResult.currency}
                </div>
              </div>
              
              <div className="bg-purple-50 rounded p-2 border border-purple-200">
                <div className="text-purple-700 text-xs font-medium">Valuta</div>
                <div className="text-purple-900 text-sm font-bold">
                  {generationResult.currency}
                </div>
              </div>
              
              <div className="bg-orange-50 rounded p-2 border border-orange-200">
                <div className="text-orange-700 text-xs font-medium">Idő</div>
                <div className="text-orange-900 text-xs font-medium">
                  {new Date(generationResult.generatedAt).toLocaleTimeString('hu-HU')}
                </div>
              </div>
            </div>
          )}

          {/* Compact Validation Results */}
          {generationResult && (generationResult.errors?.length || generationResult.warnings?.length) && (
            <div className="mt-2 space-y-1">
              {generationResult.errors?.map((error, index) => (
                <div key={index} className="flex items-center space-x-1 text-red-700 bg-red-50 px-2 py-1 rounded text-xs border border-red-200">
                  <XCircleIcon className="w-3 h-3 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              ))}
              
              {generationResult.warnings?.map((warning, index) => (
                <div key={index} className="flex items-center space-x-1 text-orange-700 bg-orange-50 px-2 py-1 rounded text-xs border border-orange-200">
                  <ExclamationTriangleIcon className="w-3 h-3 flex-shrink-0" />
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compact Recent Requests */}
      <div className="bg-white rounded-lg p-3 border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <ClockIcon className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-800">Kérések</h4>
          </div>
          <button
            onClick={loadRequests}
            className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-xs font-medium"
          >
            Frissítés
          </button>
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <ClockIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-xs font-medium text-gray-400">Nincs kérés</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div className="flex-shrink-0 text-xs">
                    {getStatusIcon(request.status)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-800 text-xs">
                        {getCurrencyIcon(request.currency)} {request.currency}
                      </span>
                      <span className="text-gray-600 text-xs">
                        {request.year}/{request.month.toString().padStart(2, '0')}
                      </span>
                      {request.provider && (
                        <span className={`px-1 py-0.5 text-xs rounded font-medium ${getProviderColor(request.provider)}`}>
                          {request.provider.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span className={`px-2 py-0.5 rounded font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                      <span className="flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        {formatDate(request.created_at)}
                      </span>
                    </div>
                    {request.error && (
                      <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-700 font-medium">Hiba:</p>
                        <p className="text-xs text-red-600">{request.error}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {request.status === 'completed' && request.result && (
                  <div className="text-right">
                    <div className="text-xs font-bold text-green-600">✅</div>
                    <div className="text-xs text-gray-500">
                      {request.result.record_count}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderDownloadTab = () => (
    <div className="p-3">
      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded">
          <div className="flex items-center space-x-1">
            <XCircleIcon className="w-3 h-3 text-red-600" />
            <p className="text-red-700 font-medium text-xs">{error}</p>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <DocumentIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-400 mb-1">Nincs fájl</p>
          <p className="text-xs text-gray-400">Generálj először XML fájlokat</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center space-x-2">
                <div className="p-1 rounded bg-blue-100 group-hover:bg-blue-200 transition-colors">
                  {getFileIcon(file.type)}
                </div>
                <div>
                  <div className="font-medium text-gray-800 text-xs mb-1">{file.name}</div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span className="font-medium">{file.humanSize}</span>
                    <span className={`px-1 py-0.5 rounded font-medium ${getFileTypeColor(file.type)}`}>
                      {file.type.toUpperCase()}
                    </span>
                    <span className="flex items-center">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      {formatDate(file.lastModified)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleDownload(file)}
                  disabled={downloadingFile === file.name}
                  className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                >
                  {downloadingFile === file.name ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>...</span>
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="w-3 h-3" />
                      <span>Letöltés</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDelete(file)}
                  disabled={deletingFile === file.name}
                  className="flex items-center space-x-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                >
                  {deletingFile === file.name ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-3 h-3" />
                      <span>Törlés</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-md p-4 ${className}`}>
        <div className="flex items-center justify-center h-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <span className="text-gray-600 font-medium text-sm">
              {activeTab === 'generate' ? 'Kérések betöltése...' : 'Fájlok betöltése...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-md ${className}`}>
      {/* Compact Tab Navigation */}
      <div className="border-b border-gray-200 bg-gray-50 rounded-t-xl">
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex space-x-1 bg-white rounded p-0.5">
              <button
                onClick={() => setActiveTab('generate')}
                className={`px-3 py-1.5 font-medium rounded text-sm transition-colors ${
                  activeTab === 'generate'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <SparklesIcon className="w-3 h-3" />
                  <span>Generálás</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('download')}
                className={`px-3 py-1.5 font-medium rounded text-sm transition-colors ${
                  activeTab === 'download'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <ArrowDownTrayIcon className="w-3 h-3" />
                  <span>Letöltés</span>
                  {files.length > 0 && (
                    <span className="px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-bold">
                      {files.length}
                    </span>
                  )}
                </div>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <DocumentIcon className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-bold text-gray-800">Clear Admin XML</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'generate' ? renderGenerateTab() : renderDownloadTab()}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Fájl törlése"
        deleteMessage={fileToDelete ? 
          `Biztosan törölni szeretnéd ezt a fájlt?\n\n"${fileToDelete.name}"` : 
          'Biztosan törölni szeretnéd ezt a fájlt?'}
        deleteWarning="Ez a művelet nem vonható vissza. A fájl véglegesen törlődik a rendszerből."
      />
    </div>
  );
};

export default ClearAdminDownloader;